import React from 'react';

import { useHeroVideoPlayback } from './heroVideoPlayback';

/**
 * ### resolveDimLayerClass
 *
 * Picks the single opacity and transition pair the dim layer renders with.
 * Emitting both states as utilities would leave the winner to CSS output order,
 * so the choice is made here instead.
 *
 * @param isVideoVisible Whether the video has reached a playing state
 * @returns The class string for the dim layer in that state
 */
function resolveDimLayerClass(isVideoVisible: boolean): string {
  if (isVideoVisible) {
    return 'opacity-0 transition-opacity duration-200 ease-[ease]';
  }

  return 'opacity-100 transition-none';
}

/**
 * ### HeroVideoBackdrop
 *
 * Renders the hero background video and the black layer that covers it until
 * playback starts. Sits behind the hero content and never delays it: the text
 * is painted over a solid black ground while the video is still loading.
 *
 * @returns The rendered backdrop element
 * @see {@link useHeroVideoPlayback} Owns the autoplay retries this backdrop reacts to
 */
export default function HeroVideoBackdrop() {
  const {
    attemptPlayback,
    playbackState,
    setEmptied,
    setError,
    setPaused,
    setPlaying,
    setStalled,
    setWaiting,
    videoRef,
  } = useHeroVideoPlayback();

  return (
    // ::after is the radial dim that keeps the headline legible over the video.
    // Light in the middle on purpose: the hero carries one line of text now, so
    // the video is meant to be seen rather than sit behind a paragraph.
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden after:absolute after:inset-0 after:bg-[radial-gradient(174.48%_100%_at_50%_0%,rgba(0,0,0,0.2)_79.5%,#000_100%)] after:content-['']"
      aria-hidden="true"
    >
      {/* Decorative background video, so there is no caption track to offer. */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full max-w-none overflow-hidden object-cover object-center [transform:translateZ(0)]"
        aria-hidden="true"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        onCanPlay={attemptPlayback}
        onEmptied={setEmptied}
        onError={setError}
        onPause={setPaused}
        onPlaying={setPlaying}
        onStalled={setStalled}
        onWaiting={setWaiting}
      >
        <source
          src="/images/apps/home/visual_video_01_74c6ac6d.webm"
          type="video/webm"
        />
        <source src="/images/apps/home/visual_video_01.mp4" type="video/mp4" />
      </video>
      <div
        className={`absolute inset-0 bg-black ${resolveDimLayerClass(
          playbackState === 'playing',
        )}`}
      />
    </div>
  );
}
